import { useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { Button, Stack, Typography } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import { formatNumber } from "@/features/shared";
import { ExpenseDialog } from "@/features/ui";

export const ExpensesSection = () => {
  const { control, getValues } = useFormContext();
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: "salesInvoiceExpenses",
  });
  const seller = useWatch({ control, name: "seller" });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const handleAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleSave = (data) => {
    if (editingIndex !== null) {
      update(editingIndex, data);
    } else {
      append(data);
    }
  };

  const columns = [
    {
      field: "outletChartOfAccount",
      headerName: "Akun",
      flex: 2,
      valueGetter: (value, row) => {
        const account = row.outletChartOfAccount;
        return account ? `${account.code} - ${account.name}` : "";
      },
    },
    {
      field: "amount",
      headerName: "Nominal",
      flex: 1,
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "returnedAmount",
      headerName: "Retur",
      flex: 1,
      type: "number",
      valueGetter: (_, row) => row.returnedAmount || 0,
      valueFormatter: (value) => (value > 0 ? formatNumber(value) : "-"),
    },
    {
      field: "note",
      headerName: "Catatan",
      flex: 2,
    },
    {
      field: "actions",
      type: "actions",
      getActions: (params) => {
        const index = fields.findIndex((f) => f.id === params.id);
        // Returned expenses are locked: editing/deleting them would break the
        // already-recorded sales return.
        const isReturned = params.row.returnedAmount > 0;
        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Ubah"
            onClick={() => handleEdit(index)}
            disabled={isReturned}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Hapus"
            onClick={() => remove(index)}
            color="error"
            disabled={isReturned}
          />,
        ];
      },
    },
  ];

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center"
        }}>
        <Typography variant="h6">Biaya Lain-lain</Typography>
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={handleAdd}
          disabled={!seller?.id}
        >
          Tambah Biaya
        </Button>
      </Stack>
      <DataGrid
        rows={fields}
        columns={columns}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        hideFooter
      />
      {dialogOpen && (
        <ExpenseDialog
          outletId={seller?.id}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleSave}
          // Pass the form values, not the useFieldArray `fields` view — the
          // latter overwrites `id` with RHF's generated key, which would then
          // be written back as the row's id on save.
          defaultValues={
            editingIndex !== null
              ? getValues(`salesInvoiceExpenses.${editingIndex}`)
              : null
          }
        />
      )}
    </Stack>
  );
};
